import React from 'react'
import { motion } from 'framer-motion'

/**
 * Hero. Phase 18-Z7 redesign (2026-05-19): premium tech hero with
 * an SVG abstract background (perspective grid + glow orbs + node
 * network). data-cms-field attrs preserved for CMS inline editing.
 */
export default function Hero({ content }) {
    const hero = content?.hero || {}
    const bg = hero.backgroundUrl
    const tagline = content?.site?.tagline || 'Korea Virtual Sports Association'

    return (
        <section className="relative overflow-hidden bg-ink-900 text-white">
            {/* SVG abstract tech background */}
            <div className="absolute inset-0" aria-hidden>
                <svg className="w-full h-full" viewBox="0 0 1440 820" preserveAspectRatio="xMidYMid slice">
                    <defs>
                        <radialGradient id="orbBlue" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.55" />
                            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                        </radialGradient>
                        <radialGradient id="orbCyan" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.45" />
                            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
                        </radialGradient>
                        <linearGradient id="gridFade" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#1e3a8a" stopOpacity="0" />
                            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.5" />
                        </linearGradient>
                    </defs>
                    {/* base */}
                    <rect width="1440" height="820" fill="#060912" />
                    {/* glow orbs */}
                    <circle cx="1180" cy="200" r="420" fill="url(#orbBlue)" className="animate-pulse-glow" />
                    <circle cx="240" cy="620" r="360" fill="url(#orbCyan)" className="animate-pulse-glow" />
                    {/* perspective grid floor */}
                    <g stroke="url(#gridFade)" strokeWidth="1" fill="none" opacity="0.7">
                        {Array.from({ length: 14 }).map((_, i) => (
                            <line key={`h${i}`} x1="0" y1={460 + i * 26} x2="1440" y2={460 + i * 26} />
                        ))}
                        {Array.from({ length: 25 }).map((_, i) => {
                            const x = i * 60
                            return <line key={`v${i}`} x1={x} y1="460" x2={(x - 720) * 2.2 + 720} y2="820" />
                        })}
                    </g>
                    {/* node network */}
                    <g>
                        {[
                            [320, 170], [520, 110], [690, 230], [860, 130],
                            [1040, 250], [1180, 150], [430, 300], [760, 360],
                        ].map(([x, y], i) => (
                            <circle key={`n${i}`} cx={x} cy={y} r={i % 3 === 0 ? 4 : 2.5}
                                fill="#60a5fa" opacity="0.9" />
                        ))}
                        <g stroke="#60a5fa" strokeWidth="1" opacity="0.3">
                            <line x1="320" y1="170" x2="520" y2="110" />
                            <line x1="520" y1="110" x2="690" y2="230" />
                            <line x1="690" y1="230" x2="860" y2="130" />
                            <line x1="860" y1="130" x2="1040" y2="250" />
                            <line x1="1040" y1="250" x2="1180" y2="150" />
                            <line x1="430" y1="300" x2="690" y2="230" />
                            <line x1="760" y1="360" x2="690" y2="230" />
                        </g>
                    </g>
                </svg>
            </div>

            {/* optional CMS background image overlay */}
            {bg && (
                <motion.div
                    data-cms-field="hero.backgroundUrl"
                    initial={{ scale: 1.15, opacity: 0 }}
                    animate={{ scale: 1, opacity: 0.4 }}
                    transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${bg})` }}
                />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/60 to-transparent" />

            {/* content */}
            <div className="relative container-fluid py-32 md:py-44 lg:py-52">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="inline-flex items-center gap-2 px-4 py-1.5 mb-7 rounded-full border border-brand-400/40 bg-brand-500/10 text-brand-200 text-xs md:text-sm font-semibold tracking-widest uppercase"
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse-glow" />
                    {tagline}
                </motion.div>
                <motion.h1
                    data-cms-field="hero.headline"
                    initial={{ opacity: 0, y: 32 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight max-w-4xl leading-[1.08]"
                >
                    <span className="gradient-text text-glow">{hero.headline || ''}</span>
                </motion.h1>
                <motion.p
                    data-cms-field="hero.subheadline"
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-7 text-base md:text-xl text-slate-300 max-w-2xl leading-relaxed"
                >
                    {hero.subheadline || ''}
                </motion.p>
                {hero.ctaLabel && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.6, type: 'spring', stiffness: 110 }}
                        className="mt-10 flex flex-wrap gap-4"
                    >
                        <a
                            data-cms-field="hero.ctaLabel"
                            href={hero.ctaHref || '#'}
                            className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-brand-500 to-cyan-500 text-white font-semibold shadow-xl shadow-brand-500/30 hover:shadow-2xl hover:shadow-brand-500/50 hover:scale-[1.03] active:scale-[0.98] transition-all"
                        >
                            {hero.ctaLabel}
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                className="group-hover:translate-x-1 transition-transform">
                                <path d="M5 12h14M13 5l7 7-7 7" />
                            </svg>
                        </a>
                    </motion.div>
                )}
            </div>

            {/* scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.6 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50"
                aria-hidden
            >
                <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12l7 7 7-7" />
                    </svg>
                </motion.div>
            </motion.div>
        </section>
    )
}
